import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner, EmptyState } from "@/components/common";
import { LevelBadge } from "@/components/LevelBadge";
import { AdminLayout, btn, btnGhost, inp, lbl } from "@/pages/admin/adminShared";
import { formatDate, todayISO } from "@/lib/format";
import { Search, Check, Trash2, CalendarPlus } from "lucide-react";

export default function ManageAssignments() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [classIds, setClassIds] = useState([]);
  const [date, setDate] = useState(todayISO());
  const [note, setNote] = useState("");
  const [holdUntilEnd, setHoldUntilEnd] = useState(true);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");
  const [viewClass, setViewClass] = useState("");

  const { data: classes } = useQuery({ queryKey: ["classes"], queryFn: async () => (await api.get("/classes")).data });
  const { data: dances } = useQuery({ queryKey: ["admin-dances"], queryFn: async () => (await api.get("/dances?limit=1000")).data });
  const { data: existing, refetch: refetchExisting } = useQuery({
    queryKey: ["assignments", viewClass], enabled: !!viewClass,
    queryFn: async () => (await api.get(`/admin/classes/${viewClass}/assignments`)).data,
  });

  const toggleClass = (id) => setClassIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const toggleDance = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const filtered = (dances || []).filter((d) => !q || d.name.toLowerCase().includes(q.toLowerCase()));

  const save = async () => {
    if (!classIds.length || !selected.length) { toast.error("Pick at least one class and one dance"); return; }
    try {
      const { data } = await api.post("/admin/assignments", { class_ids: classIds, dance_ids: selected, post_date: date, note, hold_until_class_end: holdUntilEnd });
      toast.success(`Added ${data.created} dance postings`);
      setSelected([]); setNote("");
      qc.invalidateQueries({ queryKey: ["assignments"] }); refetchExisting();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const removeAssignment = async (id) => { await api.delete(`/admin/assignments/${id}`); refetchExisting(); toast("Posting removed"); };
  const setSchedule = async (a, hold) => {
    try {
      await api.patch(`/admin/assignments/${a.id}`, { hold_until_class_end: hold });
      toast.success(hold ? "Held until the class finishes" : "Published now — dancers can see it");
      refetchExisting();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <AdminLayout role={user?.role} title="Add dances to classes" subtitle="Post this week's dances. Assign one or many dances to one or many classes on a chosen date — the dance stays a single central record.">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-bold text-[#2D1E18]">1. Choose classes &amp; date</h2>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="assign-classes">
            {(classes || []).map((c) => (
              <button key={c.id} onClick={() => toggleClass(c.id)} data-testid={`assign-class-${c.slug}`}
                className={`rounded-full px-3 py-1.5 text-sm font-semibold ${classIds.includes(c.id) ? "bg-[#C85A32] text-white" : "border border-border bg-white text-[#5C4A42]"}`}>{c.name}</button>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div><label className={lbl}>Posting date</label><input type="date" className={inp} value={date} onChange={(e) => setDate(e.target.value)} data-testid="assign-date" /></div>
            <div><label className={lbl}>Note (optional)</label><input className={inp} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. New this week" /></div>
          </div>
          <label className="mt-3 flex cursor-pointer items-start gap-2.5 rounded-xl border border-border bg-secondary/40 p-3" data-testid="assign-hold-toggle">
            <input type="checkbox" checked={holdUntilEnd} onChange={(e) => setHoldUntilEnd(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#C85A32]" />
            <span className="text-sm text-[#5C4A42]"><span className="font-semibold text-[#2D1E18]">Hold until the class finishes</span><br />Dancers won't see these dances until after each class's finish time on the posting date. Leave ticked to post tonight's dances early without spoiling the surprise.</span>
          </label>

          <h2 className="mt-6 font-display text-lg font-bold text-[#2D1E18]">2. Pick dances</h2>
          <div className="mt-2 flex items-center gap-2 rounded-xl border border-border px-3"><Search size={16} className="text-[#6E5B51]" /><input className="w-full bg-transparent py-2 outline-none" placeholder="Search dances" value={q} onChange={(e) => setQ(e.target.value)} data-testid="assign-dance-search" /></div>
          <div className="mt-3 max-h-72 space-y-1 overflow-y-auto">
            {filtered.map((d) => (
              <button key={d.id} onClick={() => toggleDance(d.id)} data-testid={`assign-dance-${d.slug}`}
                className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left ${selected.includes(d.id) ? "bg-[#FBEBE4]" : "hover:bg-secondary"}`}>
                <span className="flex items-center gap-2"><span className="font-semibold text-[#2D1E18]">{d.name}</span><LevelBadge level={d.level} /></span>
                {selected.includes(d.id) && <Check size={16} className="text-[#B04B26]" />}
              </button>
            ))}
          </div>
          <button className={`${btn} mt-4 w-full justify-center`} onClick={save} data-testid="assign-save">
            <CalendarPlus size={16} /> Add {selected.length || ""} dance{selected.length === 1 ? "" : "s"} to {classIds.length || ""} class{classIds.length === 1 ? "" : "es"}
          </button>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-bold text-[#2D1E18]">Existing postings</h2>
          <select className={`${inp} mt-3`} value={viewClass} onChange={(e) => setViewClass(e.target.value)} data-testid="view-class-assignments">
            <option value="">Select a class to view…</option>
            {(classes || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="mt-4">
            {!viewClass ? <p className="text-sm text-[#6E5B51]">Choose a class to see what's been posted.</p> :
              !existing ? <Spinner /> : !existing.length ? <EmptyState title="Nothing posted yet" /> : (
                <div className="space-y-2">
                  {existing.map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-2 rounded-lg border border-border p-2.5" data-testid={`assignment-${a.id}`}>
                      <div><span className="font-semibold text-[#2D1E18]">{a.dance_name}</span>{a.published === false && <span className="ml-2 rounded-full bg-[#FBEBE4] px-2 py-0.5 text-[10px] font-bold uppercase text-[#B04B26]" data-testid={`assignment-scheduled-${a.id}`}>Scheduled</span>}<p className="text-xs text-[#6E5B51]">{formatDate(a.post_date, { weekday: "short", year: true })}{a.published === false ? " · hidden until class ends" : ""}</p></div>
                      <div className="flex shrink-0 items-center gap-1">
                        {a.published === false
                          ? <button onClick={() => setSchedule(a, false)} data-testid={`publish-now-${a.id}`} className="rounded-lg px-2 py-1 text-xs font-semibold text-[#4A6B5D] hover:bg-secondary">Publish now</button>
                          : <button onClick={() => setSchedule(a, true)} data-testid={`hold-${a.id}`} className="rounded-lg px-2 py-1 text-xs font-semibold text-[#B04B26] hover:bg-secondary">Hold to class end</button>}
                        <button onClick={() => removeAssignment(a.id)} className="rounded-lg p-1.5 text-[#6E5B51] hover:text-[#991B1B]" aria-label="Remove"><Trash2 size={15} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
